import { useState } from "react";
import { ArrowRight, ArrowLeft, Check, Store, FileText, MapPin, Settings2, Loader2, LogOut } from "lucide-react";
import { api, errorMessage } from "../api";
import { useAuth } from "./AuthContext";

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar","Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry"];

const STEPS = [
  { id: 1, title: "Outlet", icon: Store, desc: "Your restaurant's identity" },
  { id: 2, title: "Compliance", icon: FileText, desc: "GSTIN & FSSAI details" },
  { id: 3, title: "Location", icon: MapPin, desc: "Where you serve guests" },
  { id: 4, title: "Operations", icon: Settings2, desc: "Channels, GST, HSN defaults" },
];

export default function OutletSetup() {
  const { user, refreshUser, refreshOutlet, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    display_name: "",
    legal_name: "",
    gstin: "",
    fssai: "",
    state: "Delhi",
    address: "",
    city: "",
    pincode: "",
    owner_name: user?.name || "",
    contact_phone: "",
    contact_email: user?.email || "",
    default_gst_rate: 5,
    default_hsn_sac: "2106",
    sales_channels: ["Dine-in", "Takeaway", "Delivery"],
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (c) =>
    setForm((f) => ({ ...f, sales_channels: f.sales_channels.includes(c) ? f.sales_channels.filter((x) => x !== c) : [...f.sales_channels, c] }));

  const next = () => setStep((s) => Math.min(STEPS.length, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const [trial, setTrial] = useState({ plan_id: "professional", edition: "restaurant", cycle: "monthly" });
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/outlet", form);
      await api.post("/subscribe/trial-plan", trial).catch((e) => window.alert(`Your outlet is saved, but the trial plan couldn't be set: ${errorMessage(e)}`));
      await refreshOutlet();
      await refreshUser();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const canProceed = () => {
    if (step === 1) return !!form.display_name;
    if (step === 2) return true; // optional for trial — can add later
    if (step === 3) return !!form.city && !!form.address;
    return form.sales_channels.length > 0;
  };

  return (
    <div className="setup-shell" data-testid="outlet-setup">
      <aside className="setup-side">
        <div className="setup-brand">
          <div className="brand-mark" style={{display:"grid",placeItems:"center"}}>F</div>
          <div>
            <b style={{fontSize:11,letterSpacing:".08em"}}>FYNDINE</b>
            <span style={{display:"block",color:"var(--muted)",fontSize:10,marginTop:3}}>RESTAURANT OS</span>
          </div>
        </div>
        <h2 className="setup-title">Let's set up your outlet.</h2>
        <p className="setup-lede">Four quick steps and your kitchen workspace is ready for service.</p>
        <ol className="setup-steps">
          {STEPS.map((s) => (
            <li key={s.id} className={step === s.id ? "active" : step > s.id ? "done" : ""} data-testid={`step-indicator-${s.id}`}>
              <span className="setup-step-icon">{step > s.id ? <Check size={14}/> : <s.icon size={14}/>}</span>
              <div><b>{s.title}</b><small>{s.desc}</small></div>
            </li>
          ))}
        </ol>
        <button className="setup-logout" onClick={logout} data-testid="setup-logout"><LogOut size={14}/> Sign out</button>
      </aside>

      <main className="setup-main">
        <div className="setup-progress"><i style={{width: `${(step/STEPS.length)*100}%`}}/></div>
        <span className="overline">STEP {step} OF {STEPS.length}</span>
        <h1 className="setup-h1">{STEPS[step-1].title}</h1>
        <p className="setup-sub">{STEPS[step-1].desc}</p>

        {step === 1 && (
          <div className="setup-form">
            <label>Restaurant display name<input required value={form.display_name} onChange={e=>upd("display_name",e.target.value)} placeholder="e.g. Saffron & Steel Delhi Cantt" data-testid="outlet-display-name"/></label>
            <label>Legal entity name<input value={form.legal_name} onChange={e=>upd("legal_name",e.target.value)} placeholder="e.g. Saffron Hospitality Pvt Ltd" data-testid="outlet-legal-name"/></label>
            <div className="setup-grid">
              <label>Owner name<input value={form.owner_name} onChange={e=>upd("owner_name",e.target.value)} placeholder="Owner's full name" data-testid="outlet-owner-name"/></label>
              <label>Contact email<input type="email" value={form.contact_email} onChange={e=>upd("contact_email",e.target.value)} placeholder="billing@restaurant.in" data-testid="outlet-contact-email"/></label>
            </div>
            <label>Contact phone<input value={form.contact_phone} onChange={e=>upd("contact_phone",e.target.value)} placeholder="Enter 10-digit mobile number" data-testid="outlet-contact-phone"/></label>
          </div>
        )}

        {step === 2 && (
          <div className="setup-form">
            <label>GSTIN (15 characters, optional)<input value={form.gstin} onChange={e=>upd("gstin", e.target.value.toUpperCase().slice(0,15))} placeholder="07AAACS1234F1Z5" maxLength={15} data-testid="outlet-gstin"/></label>
            <label>FSSAI license number (14 digits, optional)<input value={form.fssai} onChange={e=>upd("fssai", e.target.value.replace(/\D/g,"").slice(0,14))} placeholder="12345678901234" maxLength={14} data-testid="outlet-fssai"/></label>
            <p className="form-hint">GSTIN and FSSAI are printed on every GST-compliant customer bill. You can add or update these any time from Branch Settings.</p>
          </div>
        )}

        {step === 3 && (
          <div className="setup-form">
            <label>Street address<textarea required value={form.address} onChange={e=>upd("address",e.target.value)} placeholder="Shop no. / Floor / Street / Area" data-testid="outlet-address"/></label>
            <div className="setup-grid">
              <label>City<input required value={form.city} onChange={e=>upd("city",e.target.value)} placeholder="e.g. New Delhi" data-testid="outlet-city"/></label>
              <label>Pincode<input value={form.pincode} onChange={e=>upd("pincode", e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="110010" maxLength={6} data-testid="outlet-pincode"/></label>
            </div>
            <label>State<select value={form.state} onChange={e=>upd("state",e.target.value)} data-testid="outlet-state">{STATES.map(s=><option key={s}>{s}</option>)}</select></label>
          </div>
        )}

        {step === 4 && (
          <div className="setup-form">
            <label>Default GST rate<select value={form.default_gst_rate} onChange={e=>upd("default_gst_rate", Number(e.target.value))} data-testid="outlet-default-gst">
              <option value={5}>5% (Non-AC restaurants / standard food)</option>
              <option value={12}>12%</option>
              <option value={18}>18% (AC / licensed liquor / premium)</option>
              <option value={28}>28%</option>
            </select></label>
            <label>Default HSN / SAC code<input value={form.default_hsn_sac} onChange={e=>upd("default_hsn_sac", e.target.value)} placeholder="2106 (prepared foods)" data-testid="outlet-default-hsn"/></label>
            <div>
              <span className="field-label">Enabled sales channels</span>
              <div className="chip-row">
                {["Dine-in","Takeaway","Delivery","Catering","Online aggregator"].map(c => (
                  <button type="button" key={c} className={form.sales_channels.includes(c) ? "chip active" : "chip"} onClick={()=>toggleChannel(c)} data-testid={`channel-${c.toLowerCase().replace(/[^a-z]/g,"-")}`}>
                    {form.sales_channels.includes(c) && <Check size={12}/>} {c}
                  </button>
                ))}
              </div>
            </div>
            <p className="form-hint">Currency locked to Indian Rupee (₹). Timezone IST. These defaults power every bill, invoice and report.</p>
            <div className="trial-pick" data-testid="setup-trial-plan">
              <span className="field-label">Your 14-day free trial — pick the plan to try</span>
              <div className="setup-form" style={{ gridTemplateColumns: "repeat(3,minmax(0,1fr))", display: "grid", gap: 10 }}>
                <label>Plan<select value={trial.plan_id} onChange={(e) => setTrial({ ...trial, plan_id: e.target.value })} data-testid="setup-trial-tier"><option value="basic">Basic</option><option value="professional">Professional</option><option value="premium">Premium</option></select></label>
                <label>Edition<select value={trial.edition} onChange={(e) => setTrial({ ...trial, edition: e.target.value })} data-testid="setup-trial-edition"><option value="restaurant">Restaurant Only</option><option value="hotel">Restaurant + Hotel</option></select></label>
                <label>Billing after trial<select value={trial.cycle} onChange={(e) => setTrial({ ...trial, cycle: e.target.value })} data-testid="setup-trial-cycle"><option value="monthly">Monthly</option><option value="half_yearly">Half-yearly</option><option value="yearly">Annual</option></select></label>
              </div>
              <p className="form-hint">Full features and limits of this plan for 14 days. Nothing is charged automatically, and you can switch plans anytime during the trial.</p>
            </div>
          </div>
        )}

        {error && <div className="auth-alert error" data-testid="setup-error">{error}</div>}

        <div className="setup-actions">
          {step > 1 && <button className="secondary-button" onClick={back} type="button" data-testid="setup-back"><ArrowLeft size={14}/> Back</button>}
          <span style={{flex:1}}/>
          {step < STEPS.length ? (
            <button className="primary-button" disabled={!canProceed()} onClick={next} type="button" data-testid="setup-next">Continue <ArrowRight size={14}/></button>
          ) : (
            <button className="primary-button" disabled={!canProceed() || busy} onClick={submit} type="button" data-testid="setup-finish">
              {busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Finish setup &amp; enter dashboard
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
